import React from 'react';
import { cx } from './cx';
import { focusRing } from './focusRing';
import Spinner from './Spinner';
import { playClickSound } from '@/utils/soundUtils';

export type ButtonVariant = 'primary' | 'payout' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Defaults to `secondary`. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, sets aria-busy and blocks clicks. */
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  /** Glow for the one main money action of a screen (primary and payout only). */
  emphasis?: boolean;
  /** Skip the click sound. */
  silent?: boolean;
}

/** Fill, text and (except ghost) the 3D edge colour that k-btn-depth draws under the button. */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-action-primary text-white hover:bg-action-primary-hover [--k-btn-edge:var(--k-btn-edge-primary)]',
  payout: 'bg-action-payout text-on-payout hover:bg-action-payout-hover [--k-btn-edge:var(--k-btn-edge-payout)]',
  secondary:
    'bg-(--k-btn-secondary-fill) text-primary border border-(--k-btn-secondary-border) hover:bg-action-neutral-hover hover:border-edge-strong [--k-btn-edge:var(--k-btn-edge-neutral)]',
  ghost: 'bg-transparent text-link hover:bg-hover',
  danger: 'bg-action-danger text-white hover:brightness-110 [--k-btn-edge:var(--k-btn-edge-danger)]',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 text-small',
  md: 'h-11 text-small',
  lg: 'h-13 text-body',
};

const PADDING: Record<ButtonSize, string> = { sm: 'px-3.5', md: 'px-4.5', lg: 'px-5.5' };

const GLOW: Partial<Record<ButtonVariant, string>> = {
  primary: 'shadow-glow-primary',
  payout: 'shadow-glow-payout',
};

/**
 * v2 button: five variants, sentence case, loading state, optional glow for the main action.
 * The style recipe (tokens.css) sets the corners (--k-radius-button), the secondary fill and edge
 * and, except on the text-only ghost, the depth (k-btn-depth: 3D edge in the variant's edge colour,
 * resting shadow and press).
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconRight,
    fullWidth,
    emphasis = false,
    silent = false,
    disabled,
    onClick,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref
) {
  const inactive = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={(e) => {
        if (inactive) return;
        if (!silent) playClickSound();
        onClick?.(e);
      }}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-(--k-radius-button) font-ui font-semibold whitespace-nowrap select-none',
        'transition-colors duration-(--k-dur-fast) active:translate-y-px',
        variant !== 'ghost' && 'k-btn-depth',
        focusRing,
        SIZES[size],
        variant === 'ghost' ? 'px-2' : PADDING[size],
        disabled && !loading
          ? 'bg-action-neutral text-disabled cursor-not-allowed [--k-btn-edge:var(--k-btn-edge-neutral)]'
          : cx(VARIANTS[variant], loading && 'cursor-progress'),
        emphasis && !disabled && GLOW[variant],
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon ? <span className="inline-flex [&>svg]:size-4">{icon}</span> : null}
      {children}
      {iconRight && !loading ? <span className="inline-flex [&>svg]:size-4">{iconRight}</span> : null}
    </button>
  );
});

export default Button;
